import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Crown, DollarSign, Users, Truck, 
  Calendar, FileText, AlertTriangle, CheckCircle, Download,
  Filter, Search, TrendingUp, Target, Activity, BarChart3,
  Clock, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Drill-down configurations for different metrics
const DRILL_DOWN_CONFIG = {
  // Protect drill-downs
  'compliance-score': {
    title: 'Compliance Score Breakdown',
    subtitle: 'Detailed compliance health analysis',
    module: 'protect',
    color: '#C0C0C0',
    icon: Shield,
  },
  'upcoming-deadlines': {
    title: 'Upcoming Deadlines',
    subtitle: 'All compliance deadlines and expiration dates',
    module: 'protect',
    color: '#F59E0B',
    icon: Calendar,
  },
  'missing-docs': {
    title: 'Missing Documents',
    subtitle: 'Documents requiring attention',
    module: 'protect',
    color: '#EF4444',
    icon: FileText,
  },
  'violations': {
    title: 'Violation History',
    subtitle: 'Complete violation and inspection records',
    module: 'protect',
    color: '#EF4444',
    icon: AlertTriangle,
  },
  'drug-alcohol-status': {
    title: 'Drug & Alcohol Compliance',
    subtitle: 'Clearinghouse status and testing records',
    module: 'protect',
    color: '#10B981',
    icon: CheckCircle,
  },
  // Grow drill-downs
  'weekly-revenue': {
    title: 'Weekly Revenue Details',
    subtitle: 'Breakdown of this week\'s earnings',
    module: 'grow',
    color: '#10B981',
    icon: DollarSign,
  },
  'rpm-analysis': {
    title: 'Rate Per Mile Analysis',
    subtitle: 'Historical RPM trends and lane performance',
    module: 'grow',
    color: '#D4AF37',
    icon: TrendingUp,
  },
  'active-loads': {
    title: 'Active Loads Detail',
    subtitle: 'All current loads with full details',
    module: 'grow',
    color: '#3B82F6',
    icon: Truck,
  },
  'broker-performance': {
    title: 'Broker Performance',
    subtitle: 'Detailed broker analytics and ratings',
    module: 'grow',
    color: '#8B5CF6',
    icon: Users,
  },
  'kpi-details': {
    title: 'KPI Metrics Detail',
    subtitle: 'In-depth performance indicators',
    module: 'grow',
    color: '#D4AF37',
    icon: Target,
  },
};

// Mock data for drill-downs
const getMockData = (metricType) => {
  switch (metricType) {
    case 'compliance-score':
      return {
        overall_score: 92,
        categories: [
          { name: 'Authority Status', score: 100, status: 'valid', details: 'DOT and MC authority active' },
          { name: 'Insurance Coverage', score: 100, status: 'valid', details: 'Liability and Cargo current' },
          { name: 'Safety Programs', score: 85, status: 'attention', details: 'Clearinghouse query due' },
          { name: 'Registrations', score: 90, status: 'valid', details: 'UCR expiring in 30 days' },
          { name: 'Driver Compliance', score: 95, status: 'valid', details: 'Medical card current' },
        ],
        history: [
          { month: 'Oct 2025', score: 88 },
          { month: 'Nov 2025', score: 90 },
          { month: 'Dec 2025', score: 89 },
          { month: 'Jan 2026', score: 91 },
          { month: 'Feb 2026', score: 92 },
        ],
      };
    case 'weekly-revenue':
      return {
        total: 8450,
        change: 12,
        loads: [
          { id: 'LD-001', route: 'Atlanta → Dallas', miles: 780, rate: 2340, rpm: 3.00, date: '02/25' },
          { id: 'LD-002', route: 'Dallas → Phoenix', miles: 870, rate: 2610, rpm: 3.00, date: '02/24' },
          { id: 'LD-003', route: 'Phoenix → LA', miles: 370, rate: 1110, rpm: 3.00, date: '02/23' },
          { id: 'LD-004', route: 'LA → Seattle', miles: 800, rate: 2390, rpm: 2.99, date: '02/22' },
        ],
        by_day: [
          { day: 'Mon', revenue: 2340 },
          { day: 'Tue', revenue: 0 },
          { day: 'Wed', revenue: 2610 },
          { day: 'Thu', revenue: 1110 },
          { day: 'Fri', revenue: 2390 },
        ],
      };
    case 'broker-performance':
      return {
        brokers: [
          { name: 'CH Robinson', loads: 12, revenue: 14500, avgRpm: 2.92, rating: 4.8, paymentScore: 98, lastLoad: '02/25' },
          { name: 'TQL', loads: 8, revenue: 9800, avgRpm: 2.85, rating: 4.5, paymentScore: 95, lastLoad: '02/24' },
          { name: 'Coyote', loads: 6, revenue: 7200, avgRpm: 2.78, rating: 4.3, paymentScore: 92, lastLoad: '02/23' },
          { name: 'Echo', loads: 4, revenue: 4800, avgRpm: 2.70, rating: 4.0, paymentScore: 88, lastLoad: '02/20' },
          { name: 'Uber Freight', loads: 3, revenue: 3600, avgRpm: 2.82, rating: 4.2, paymentScore: 100, lastLoad: '02/22' },
        ],
        summary: {
          total_brokers: 5,
          avg_rating: 4.36,
          avg_payment: 94.6,
          top_performer: 'CH Robinson',
        },
      };
    default:
      return { message: 'No data available for this metric' };
  }
};

// Compliance Score Detail Component
const ComplianceScoreDetail = ({ data }) => (
  <div className="space-y-6">
    {/* Overall Score */}
    <div className="bg-[#121212] border border-white/10 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-white font-bold uppercase tracking-wider">Overall Health Score</h3>
        <span className={`text-4xl font-bold ${
          data.overall_score >= 80 ? 'text-green-400' : 
          data.overall_score >= 60 ? 'text-amber-400' : 'text-red-400'
        }`}>
          {data.overall_score}%
        </span>
      </div>
      <div className="w-full h-3 bg-[#0A0A0A] rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full ${
            data.overall_score >= 80 ? 'bg-green-500' : 
            data.overall_score >= 60 ? 'bg-amber-500' : 'bg-red-500'
          }`}
          style={{ width: `${data.overall_score}%` }}
        />
      </div>
    </div>

    {/* Category Breakdown */}
    <div className="bg-[#121212] border border-white/10 p-6">
      <h3 className="text-white font-bold uppercase tracking-wider mb-4">Category Breakdown</h3>
      <div className="space-y-4">
        {data.categories?.map((cat, idx) => (
          <div key={idx} className="flex items-center justify-between p-4 bg-[#0A0A0A] border-l-4" style={{
            borderColor: cat.score >= 90 ? '#10B981' : cat.score >= 70 ? '#F59E0B' : '#EF4444'
          }}>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-white font-medium">{cat.name}</span>
                <span className={`font-bold ${
                  cat.score >= 90 ? 'text-green-400' : 
                  cat.score >= 70 ? 'text-amber-400' : 'text-red-400'
                }`}>
                  {cat.score}%
                </span>
              </div>
              <p className="text-white/50 text-sm">{cat.details}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* History Chart */}
    <div className="bg-[#121212] border border-white/10 p-6">
      <h3 className="text-white font-bold uppercase tracking-wider mb-4">Score History</h3>
      <div className="h-48 flex items-end justify-around gap-2">
        {data.history?.map((item, idx) => (
          <div key={idx} className="flex-1 flex flex-col items-center">
            <div 
              className="w-full bg-gradient-to-t from-green-500 to-green-400 rounded-t"
              style={{ height: `${(item.score / 100) * 100}%` }}
            />
            <span className="text-white/40 text-xs mt-2">{item.month.split(' ')[0]}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Weekly Revenue Detail Component
const WeeklyRevenueDetail = ({ data }) => (
  <div className="space-y-6">
    {/* Summary */}
    <div className="bg-gradient-to-r from-[#121212] to-[#1a1a1a] border border-white/10 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/50 text-sm uppercase">Total Weekly Revenue</p>
          <p className="text-4xl font-bold text-white mt-1">${data.total?.toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-2 text-green-400">
          <TrendingUp className="w-5 h-5" />
          <span className="font-bold">+{data.change}%</span>
        </div>
      </div>
    </div>

    {/* Daily Breakdown */}
    <div className="bg-[#121212] border border-white/10 p-6">
      <h3 className="text-white font-bold uppercase tracking-wider mb-4">Daily Breakdown</h3>
      <div className="h-48 flex items-end justify-around gap-4">
        {data.by_day?.map((day, idx) => (
          <div key={idx} className="flex-1 flex flex-col items-center">
            <p className="text-green-400 font-bold mb-2">${day.revenue.toLocaleString()}</p>
            <div 
              className="w-full bg-[#D4AF37] rounded-t"
              style={{ height: day.revenue > 0 ? `${(day.revenue / 3000) * 100}%` : '4px', minHeight: '4px' }}
            />
            <span className="text-white/40 text-xs mt-2">{day.day}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Load Details */}
    <div className="bg-[#121212] border border-white/10">
      <div className="p-4 border-b border-white/10">
        <h3 className="text-white font-bold uppercase tracking-wider">Loads This Week</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#0A0A0A]">
            <tr className="text-left text-white/50 text-xs uppercase">
              <th className="p-4">Load ID</th>
              <th className="p-4">Route</th>
              <th className="p-4">Miles</th>
              <th className="p-4">Rate</th>
              <th className="p-4">RPM</th>
              <th className="p-4">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.loads?.map((load, idx) => (
              <tr key={idx} className="hover:bg-white/5">
                <td className="p-4 text-[#D4AF37] font-medium">{load.id}</td>
                <td className="p-4 text-white">{load.route}</td>
                <td className="p-4 text-white">{load.miles}</td>
                <td className="p-4 text-green-400 font-bold">${load.rate.toLocaleString()}</td>
                <td className="p-4 text-white">${load.rpm.toFixed(2)}</td>
                <td className="p-4 text-white/70">{load.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Broker Performance Detail Component
const BrokerPerformanceDetail = ({ data }) => (
  <div className="space-y-6">
    {/* Summary Cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-[#121212] border border-white/10 p-4">
        <p className="text-white/50 text-xs uppercase">Total Brokers</p>
        <p className="text-2xl font-bold text-white">{data.summary?.total_brokers}</p>
      </div>
      <div className="bg-[#121212] border border-white/10 p-4">
        <p className="text-white/50 text-xs uppercase">Avg Rating</p>
        <p className="text-2xl font-bold text-[#D4AF37]">{data.summary?.avg_rating}</p>
      </div>
      <div className="bg-[#121212] border border-white/10 p-4">
        <p className="text-white/50 text-xs uppercase">Avg Payment %</p>
        <p className="text-2xl font-bold text-green-400">{data.summary?.avg_payment}%</p>
      </div>
      <div className="bg-[#121212] border border-white/10 p-4">
        <p className="text-white/50 text-xs uppercase">Top Performer</p>
        <p className="text-lg font-bold text-white truncate">{data.summary?.top_performer}</p>
      </div>
    </div>

    {/* Broker Table */}
    <div className="bg-[#121212] border border-white/10">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <h3 className="text-white font-bold uppercase tracking-wider">All Brokers</h3>
        <button className="px-4 py-2 bg-[#D4AF37] text-black text-sm font-medium">
          <Download className="w-4 h-4 inline mr-2" />
          Export Report
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#0A0A0A]">
            <tr className="text-left text-white/50 text-xs uppercase">
              <th className="p-4">Broker</th>
              <th className="p-4">Loads</th>
              <th className="p-4">Revenue</th>
              <th className="p-4">Avg RPM</th>
              <th className="p-4">Rating</th>
              <th className="p-4">Payment %</th>
              <th className="p-4">Last Load</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.brokers?.map((broker, idx) => (
              <tr key={idx} className="hover:bg-white/5">
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-white font-medium">{broker.name}</span>
                  </div>
                </td>
                <td className="p-4 text-white">{broker.loads}</td>
                <td className="p-4 text-green-400 font-bold">${broker.revenue.toLocaleString()}</td>
                <td className="p-4 text-white">${broker.avgRpm.toFixed(2)}</td>
                <td className="p-4">
                  <span className="text-[#D4AF37]">★ {broker.rating}</span>
                </td>
                <td className="p-4">
                  <span className={broker.paymentScore >= 95 ? 'text-green-400' : broker.paymentScore >= 90 ? 'text-amber-400' : 'text-red-400'}>
                    {broker.paymentScore}%
                  </span>
                </td>
                <td className="p-4 text-white/70">{broker.lastLoad}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Generic Detail Component for other metrics
const GenericDetail = ({ config, data }) => (
  <div className="space-y-6">
    <div className="bg-[#121212] border border-white/10 p-8 text-center">
      <config.icon className="w-16 h-16 mx-auto mb-4" style={{ color: config.color }} />
      <h3 className="text-xl font-bold text-white mb-2">{config.title}</h3>
      <p className="text-white/50">{config.subtitle}</p>
      <p className="text-white/30 mt-4 text-sm">Detailed analytics coming soon</p>
    </div>
    
    {data && (
      <div className="bg-[#121212] border border-white/10 p-6">
        <h4 className="text-white font-bold mb-4">Available Data</h4>
        <pre className="text-white/50 text-sm overflow-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    )}
  </div>
);

// Main Drill-Down Page Component
const DrillDownPage = () => {
  const navigate = useNavigate();
  const { metricType } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const config = DRILL_DOWN_CONFIG[metricType];
  
  useEffect(() => {
    // Verify auth
    const token = localStorage.getItem('hive_token');
    if (!token) {
      navigate('/hive/login');
      return;
    }
    
    // Load data
    setIsLoading(true);
    // In production, this would fetch from API
    setTimeout(() => {
      setData(getMockData(metricType));
      setIsLoading(false);
    }, 500);
  }, [metricType, navigate]);
  
  if (!config) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Metric Not Found</h2>
          <p className="text-white/50 mb-4">The requested drill-down view doesn't exist.</p>
          <button 
            onClick={() => navigate('/hive/dashboard')}
            className="px-6 py-3 bg-[#D4AF37] text-black font-bold"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  const Icon = config.icon;
  const moduleColor = config.module === 'protect' ? '#C0C0C0' : '#D4AF37';
  
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      );
    }
    
    switch (metricType) {
      case 'compliance-score':
        return <ComplianceScoreDetail data={data} />;
      case 'weekly-revenue':
        return <WeeklyRevenueDetail data={data} />;
      case 'broker-performance':
        return <BrokerPerformanceDetail data={data} />;
      default:
        return <GenericDetail config={config} data={data} />;
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="drill-down-page">
      {/* Header */}
      <header className="bg-[#0D0D0D] border-b border-white/10 px-6 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/hive/dashboard')}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </button>
            <div className="h-6 w-px bg-white/10" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: `${moduleColor}20` }}>
                <Icon className="w-5 h-5" style={{ color: moduleColor }} />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {config.title}
                </h1>
                <p className="text-white/50 text-sm">{config.subtitle}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button className="px-4 py-2 bg-white/5 text-white/60 hover:text-white text-sm">
              <Filter className="w-4 h-4 inline mr-2" />
              Filters
            </button>
            <button className="px-4 py-2 text-black font-medium text-sm" style={{ backgroundColor: moduleColor }}>
              <Download className="w-4 h-4 inline mr-2" />
              Export
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {renderContent()}
      </main>
      
      {/* Footer */}
      <footer className="border-t border-white/5 px-6 py-4 mt-8">
        <p className="text-white/30 text-xs text-center max-w-7xl mx-auto">
          Queen B Logistics • {config.module === 'protect' ? 'Compliance Monitoring' : 'Dispatch Operations'}
        </p>
      </footer>
    </div>
  );
};

export default DrillDownPage;
